import React, { Component } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import Menu from "components/Menu";
import Switcher from "components/Switcher";
import SwitcherPane from "components/Switcher/Pane";
import ExternalClickListener from "components/ExternalClickListener";
import Chip from "components/Chip";
import QueryManagerContext from "context/QueryManager/QueryManagerContext";
import styles from "./styles.module.scss";
import MinMaxSlider from "../MinMaxSlider";

export default class SizeFilter extends Component {
  queryStringKeys = [
    "minOccupancy",
    "maxOccupancy",
    "minSqft",
    "maxSqft",
    "activeSizeFilter",
  ];

  maxOccupancy = "# of Employees";

  squareFeetTitle = "Square Feet";

  constructor(props, context) {
    super(props);
    this.committedValues = context.values;
    this.state = {
      minSqft: context.values.minSqft,
      maxSqft: context.values.maxSqft,
      minOccupancy: context.values.minOccupancy,
      maxOccupancy: context.values.maxOccupancy,
      activeSizeFilter: context.values.activeSizeFilter,
    };
  }

  componentDidUpdate() {
    const { open } = this.props;
    const { values } = this.context;
    const { minSqft, maxSqft, minOccupancy, maxOccupancy, activeSizeFilter } =
      this.state;
    const committedChanged = this.queryStringKeys.some(
      (key) => values[key] !== this.committedValues[key]
    );
    this.committedValues = values;
    if (
      (!open || committedChanged) &&
      (values.minSqft !== minSqft ||
        values.maxSqft !== maxSqft ||
        values.minOccupancy !== minOccupancy ||
        values.maxOccupancy !== maxOccupancy ||
        values.activeSizeFilter !== activeSizeFilter)
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        minSqft: values.minSqft,
        maxSqft: values.maxSqft,
        minOccupancy: values.minOccupancy,
        maxOccupancy: values.maxOccupancy,
        activeSizeFilter: values.activeSizeFilter,
      });
    }
  }

  get isDefault() {
    const { keysAreDefault, values } = this.context;
    return keysAreDefault(this.queryStringKeys, values);
  }

  get mode() {
    const { values } = this.context;
    if (values.activeSizeFilter === "SEATS") return this.maxOccupancy;

    return this.squareFeetTitle;
  }

  get minSqft() {
    const { minSqft } = this.state;
    const { values } = this.context;
    return values.minSqft === minSqft ? minSqft : values.minSqft;
  }

  get maxSqft() {
    const { maxSqft } = this.state;
    const { values } = this.context;
    return values.maxSqft === maxSqft ? maxSqft : values.maxSqft;
  }

  get chipText() {
    if (this.isDefault) return "Size";
    const { minSqft, maxSqft, minOccupancy, maxOccupancy } = this.state;
    const { values, defaultValues } = this.context;
    const newMinSqft = values.minSqft === minSqft ? minSqft : values.minSqft;
    const newMaxSqft = values.maxSqft === maxSqft ? maxSqft : values.maxSqft;
    const newMinOccupancy =
      values.minOccupancy === minOccupancy ? minOccupancy : values.minOccupancy;
    const newMaxOccupancy =
      values.maxOccupancy === maxOccupancy ? maxOccupancy : values.maxOccupancy;

    const numericMax =
      this.mode === this.maxOccupancy ? newMaxOccupancy : newMaxSqft;
    let min =
      newMinSqft < 1000 ? newMinSqft : `${Math.floor(newMinSqft / 1000)}k`;
    let max =
      newMaxSqft < 1000 ? newMaxSqft : `${Math.floor(newMaxSqft / 1000)}k`;
    if (this.mode === this.maxOccupancy) {
      min = newMinOccupancy;
      max = newMaxOccupancy;
    }
    const ceiling =
      this.mode === this.squareFeetTitle
        ? defaultValues.maxSqft
        : defaultValues.maxOccupancy;
    const ceilingIndicator = numericMax >= ceiling ? "+" : "";

    return `${min} - ${max}${ceilingIndicator}`;
  }

  handleClear = () => {
    const { defaultValues } = this.context;
    const values = {
      minSqft: defaultValues.minSqft,
      maxSqft: defaultValues.maxSqft,
      minOccupancy: defaultValues.minOccupancy,
      maxOccupancy: defaultValues.maxOccupancy,
      activeSizeFilter: "SQUARE_FEET",
    };
    this.setState(values, this.onDone);
  };

  handleSquareFeetChange = ({ min, max }) => {
    const { defaultValues } = this.context;
    this.setState({
      minSqft: min,
      maxSqft: max,
      minOccupancy: defaultValues.minOccupancy,
      maxOccupancy: defaultValues.maxOccupancy,
      activeSizeFilter: "SQUARE_FEET",
    });
  };

  handleOccupancyChange = ({ min, max }) => {
    const { defaultValues } = this.context;
    this.setState({
      minSqft: defaultValues.minSqft,
      maxSqft: defaultValues.maxSqft,
      minOccupancy: min,
      maxOccupancy: max,
      activeSizeFilter: "SEATS",
    });
  };

  onDone = (toggle = true) => {
    const { update, defaultValues } = this.context;
    const { onToggle } = this.props;
    const { minSqft, maxSqft, maxOccupancy, minOccupancy, activeSizeFilter } =
      this.state;

    update({
      minSqft: minSqft || defaultValues.minSqft,
      maxSqft: maxSqft || defaultValues.maxSqft,
      minOccupancy: minOccupancy || defaultValues.minOccupancy,
      maxOccupancy: maxOccupancy || defaultValues.maxOccupancy,
      page: 1,
      activeSizeFilter: activeSizeFilter || defaultValues.activeSizeFilter,
    });
    if (toggle) onToggle();
  };

  onSwitcherChange = (title) => {
    this.setState({
      activeSizeFilter: title === this.maxOccupancy ? "SEATS" : "SQUARE_FEET",
    });
  };

  render() {
    const { activeSizeFilter } = this.state;
    const { open, onToggle } = this.props;
    const { minSqft, maxSqft, minOccupancy, maxOccupancy } = this.state;
    const { defaultValues } = this.context;

    return (
      <ExternalClickListener onExternalClick={() => open && this.onDone()}>
        <div className={styles.filterWrapper}>
          <Chip
            data-qa="size-filter-button"
            className={cn(styles.chip, !this.isDefault && styles.chipSelected)}
            onClick={onToggle}
            active={open}
          >
            {this.chipText}
          </Chip>
          <Menu
            data-qa="size-filter-menu"
            title="Size"
            className={styles.sizeMenu}
            visible={open}
            onClear={this.handleClear}
            onClose={onToggle}
            isDefault={this.isDefault}
            onDone={this.onDone}
          >
            <Switcher
              onSwitch={this.onSwitcherChange}
              selectedPane={
                activeSizeFilter === "SEATS"
                  ? this.maxOccupancy
                  : this.squareFeetTitle
              }
            >
              <SwitcherPane
                title={this.maxOccupancy}
                default={this.mode === this.maxOccupancy}
              >
                <MinMaxSlider
                  min={minOccupancy}
                  floor={defaultValues.minOccupancy}
                  max={maxOccupancy}
                  ceiling={defaultValues.maxOccupancy}
                  onChange={this.handleOccupancyChange}
                  onSliderMove={this.handleOccupancyChange}
                />
              </SwitcherPane>
              <SwitcherPane
                title={this.squareFeetTitle}
                default={this.mode === this.squareFeetTitle}
              >
                <MinMaxSlider
                  min={minSqft}
                  floor={defaultValues.minSqft}
                  max={maxSqft}
                  ceiling={defaultValues.maxSqft}
                  onChange={this.handleSquareFeetChange}
                  onSliderMove={this.handleSquareFeetChange}
                />
              </SwitcherPane>
            </Switcher>
          </Menu>
        </div>
      </ExternalClickListener>
    );
  }
}

SizeFilter.propTypes = {
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

SizeFilter.contextType = QueryManagerContext;
